'use client';

import { FILTER_CONTROL_STYLE } from './ui/FilterPill';
import { Box, Button, Flex, Grid, HStack, Portal, Text, VStack } from '@chakra-ui/react';
import { CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useVoicePageControl } from '@/lib/voice/page-controls';
import { Popover } from '@chakra-ui/react';

export interface AnalyticsDatePreset {
  key: string;
  label: string;
  days: number;
}

export interface AnalyticsDateRangeValue {
  key: string;
  label: string;
  startDate: string;
  endDate: string;
}

interface AnalyticsDateRangePickerProps {
  value: AnalyticsDateRangeValue;
  presets: readonly AnalyticsDatePreset[];
  onChange: (value: AnalyticsDateRangeValue) => void;
  label?: string;
  showComparison?: boolean;
  disabled?: boolean;
  compact?: boolean;
  isActive?: boolean;
  iconOnly?: boolean;
  selectionMode?: 'range' | 'single';
  allowFutureDates?: boolean;
  allowClear?: boolean;
  triggerVariant?: 'filter' | 'property';
  calendarToday?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function parseDateKey(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return new Date(Number.NaN);
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

function dateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(value: string, days: number): string {
  const date = parseDateKey(value);
  date.setUTCDate(date.getUTCDate() + days);
  return dateKey(date);
}

function startOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addMonths(date: Date, months: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
}

function calendarAnchorForRange(endDate: string): Date {
  const end = parseDateKey(endDate);
  if (!Number.isFinite(end.getTime())) return startOfMonth(new Date());
  return addMonths(startOfMonth(end), -1);
}

function formatRangeDate(value: string, includeYear = false): string {
  const date = parseDateKey(value);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' as const } : {}),
    timeZone: 'UTC',
  }).format(date);
}

function formatRange(startDate: string, endDate: string): string {
  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  return `${formatRangeDate(startDate, !sameYear)} – ${formatRangeDate(endDate, true)}`;
}

export function previousAnalyticsPeriod(startDate: string, endDate: string) {
  const durationDays = Math.max(
    1,
    Math.round((parseDateKey(endDate).getTime() - parseDateKey(startDate).getTime()) / DAY_MS) + 1
  );
  const previousEnd = addDays(startDate, -1);
  return {
    startDate: addDays(previousEnd, -(durationDays - 1)),
    endDate: previousEnd,
  };
}

function calendarDays(month: Date) {
  const first = startOfMonth(month);
  const gridStart = new Date(first.getTime());
  gridStart.setUTCDate(gridStart.getUTCDate() - first.getUTCDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart.getTime());
    date.setUTCDate(date.getUTCDate() + index);
    return {
      key: dateKey(date),
      day: date.getUTCDate(),
      inMonth: date.getUTCMonth() === first.getUTCMonth(),
    };
  });
}

function MonthCalendar({
  month,
  startDate,
  endDate,
  maxDate,
  onSelect,
}: {
  month: Date;
  startDate: string;
  endDate: string;
  maxDate: string | null;
  onSelect: (date: string) => void;
}) {
  const days = useMemo(() => calendarDays(month), [month]);
  const monthLabel = new Intl.DateTimeFormat('en', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(month);

  return (
    <Box minW={0} flex="1">
      <Text textAlign="center" color="ui.text.primary" fontSize="sm" fontWeight="700" mb="2">
        {monthLabel}
      </Text>
      <Grid templateColumns="repeat(7, 1fr)" gap="0.5">
        {WEEKDAYS.map(day => (
          <Text
            key={day}
            textAlign="center"
            color="ui.text.tertiary"
            fontSize="0.625rem"
            fontWeight="700"
            py="1"
          >
            {day}
          </Text>
        ))}
        {days.map(day => {
          const isStart = day.key === startDate;
          const isEnd = day.key === endDate;
          const inRange = Boolean(
            startDate && endDate && day.key >= startDate && day.key <= endDate
          );
          const disabled = Boolean(maxDate && day.key > maxDate);
          return (
            <Button
              variant="ghost"
              p="0"
              key={day.key}
              h="32px"
              minW="30px"
              borderRadius="9px"
              color={
                isStart || isEnd
                  ? 'ui.text.primary'
                  : day.inMonth
                    ? 'ui.text.secondary'
                    : 'ui.text.tertiary'
              }
              bg={isStart || isEnd ? 'ui.surface.selected' : inRange ? 'ui.surface.input' : 'none'}
              borderWidth="1px"
              borderStyle="solid"
              borderColor={isStart || isEnd ? 'ui.border.default' : 'transparent'}
              fontSize="xs"
              fontWeight={isStart || isEnd ? '800' : '600'}
              opacity={disabled ? 0.3 : day.inMonth ? 1 : 0.52}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              aria-label={day.key}
              aria-pressed={isStart || isEnd}
              disabled={disabled}
              onClick={() => onSelect(day.key)}
              _hover={
                disabled ? undefined : { bg: 'ui.surface.selected', color: 'ui.text.primary' }
              }
              _focusVisible={{ outline: '2px solid', outlineColor: 'ui.border.focus' }}
            >
              {day.day}
            </Button>
          );
        })}
      </Grid>
    </Box>
  );
}

export function dateRangeFromPreset(
  preset: AnalyticsDatePreset,
  endDate = new Date().toISOString().slice(0, 10)
): AnalyticsDateRangeValue {
  return {
    key: preset.key,
    label: preset.label,
    startDate: addDays(endDate, -(preset.days - 1)),
    endDate,
  };
}

export function AnalyticsDateRangePicker({
  value,
  presets,
  onChange,
  label = 'Range',
  showComparison = false,
  disabled = false,
  compact = true,
  isActive = true,
  iconOnly = false,
  selectionMode = 'range',
  allowFutureDates = false,
  allowClear = false,
  triggerVariant = 'filter',
  calendarToday,
}: AnalyticsDateRangePickerProps) {
  const today = calendarToday || new Date().toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(value.startDate.slice(0, 10));
  const [draftEnd, setDraftEnd] = useState(value.endDate.slice(0, 10));
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    selectionMode === 'single'
      ? startOfMonth(parseDateKey(value.endDate || today))
      : calendarAnchorForRange(value.endDate)
  );
  const draftComparison =
    showComparison && draftStart && draftEnd ? previousAnalyticsPeriod(draftStart, draftEnd) : null;

  const voiceRef = useVoicePageControl({
    label: `${label} dates`,
    kind: 'date_range',
    value: value.key === 'custom' ? `${value.startDate}/${value.endDate}` : value.key,
    displayValue: `${value.startDate} – ${value.endDate}`,
    options: presets.map(preset => ({ value: preset.key, label: preset.label })),
    disabled: disabled || triggerVariant !== 'filter',
    apply: requested => {
      const preset = presets.find(
        item => item.key === requested || item.label.toLowerCase() === requested.toLowerCase()
      );
      let next: AnalyticsDateRangeValue;
      if (preset) next = dateRangeFromPreset(preset, today);
      else {
        const parts = requested.split('/');
        if (parts.length !== 2) throw new Error('Use YYYY-MM-DD/YYYY-MM-DD for custom dates.');
        const [startDate, endDate] = parts;
        const valid = (date: string | undefined) =>
          Boolean(
            date &&
              /^\d{4}-\d{2}-\d{2}$/.test(date) &&
              Number.isFinite(parseDateKey(date).getTime()) &&
              dateKey(parseDateKey(date)) === date
          );
        if (
          !valid(startDate) ||
          !valid(endDate) ||
          startDate > endDate ||
          (!allowFutureDates && endDate > today) ||
          (selectionMode === 'single' && startDate !== endDate)
        )
          throw new Error('Use a valid available date range as YYYY-MM-DD/YYYY-MM-DD.');
        next = { key: 'custom', label: 'Custom', startDate, endDate };
      }
      onChange(next);
      setOpen(false);
      return next.key === 'custom' ? `${next.startDate}/${next.endDate}` : next.key;
    },
  });

  const selectDay = (selected: string) => {
    if (selectionMode === 'single') {
      onChange({
        key: 'custom',
        label: formatRangeDate(selected, true),
        startDate: selected,
        endDate: selected,
      });
      setOpen(false);
      return;
    }
    if (!selectingEnd) {
      setDraftStart(selected);
      setDraftEnd(selected);
      setSelectingEnd(true);
      return;
    }
    setDraftStart(selected < draftStart ? selected : draftStart);
    setDraftEnd(selected < draftStart ? draftStart : selected);
    setSelectingEnd(false);
  };

  const applyCustomRange = () => {
    if (!draftStart || !draftEnd) return;
    onChange({
      key: 'custom',
      label: 'Custom',
      startDate: draftStart,
      endDate: draftEnd,
    });
    setOpen(false);
  };

  return (
    <HStack ref={voiceRef} gap="2" minW={0} flexShrink={0}>
      {!compact ? (
        <Text
          color="ui.text.tertiary"
          fontSize="0.6875rem"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="0.05em"
        >
          {label}
        </Text>
      ) : null}
      <Popover.Root
        open={open}
        onOpenChange={details => {
          setOpen(details.open);
          if (details.open) {
            const start = value.startDate.slice(0, 10);
            setDraftStart(start);
            setDraftEnd(value.endDate.slice(0, 10));
            setSelectingEnd(false);
            setVisibleMonth(
              selectionMode === 'single'
                ? startOfMonth(parseDateKey(value.endDate || today))
                : calendarAnchorForRange(value.endDate)
            );
          }
        }}
        positioning={{
          placement: 'bottom-start',
          strategy: 'fixed',
          flip: true,
          slide: true,
          fitViewport: true,
          gutter: 8,
          overflowPadding: 12,
        }}
      >
        <Popover.Trigger asChild>
          <Button
            {...FILTER_CONTROL_STYLE}
            variant="ghost"
            layerStyle={triggerVariant === 'property' ? undefined : 'filterPill'}
            h={triggerVariant === 'property' ? '30px' : '36px'}
            minW={iconOnly ? '36px' : undefined}
            maxW={iconOnly ? '36px' : { base: '190px', md: '250px' }}
            px={iconOnly ? '2' : triggerVariant === 'property' ? '2' : '2.5'}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            gap={iconOnly ? '0' : '1.5'}
            color={iconOnly && !isActive ? 'ui.text.tertiary' : 'ui.text.primary'}
            fontSize={compact ? '0.8125rem' : '0.875rem'}
            fontWeight="500"
            borderRadius="10px"
            flexShrink={0}
            cursor={disabled ? 'default' : 'pointer'}
            disabled={disabled}
            opacity={disabled ? 0.6 : 1}
            aria-label={`${label}: ${
              isActive
                ? selectionMode === 'single'
                  ? formatRangeDate(value.endDate, true)
                  : value.key === 'custom'
                    ? formatRange(value.startDate, value.endDate)
                    : value.label
                : selectionMode === 'single'
                  ? 'No date'
                  : 'All time'
            }`}
            title={
              iconOnly
                ? `${label}: ${isActive ? value.label : selectionMode === 'single' ? 'No date' : 'All time'}`
                : undefined
            }
            _hover={{ bg: 'ui.surface.selected', borderColor: 'ui.border.default' }}
            _focusVisible={{ outline: '2px solid', outlineColor: 'ui.border.focus' }}
          >
            {triggerVariant === 'property' ? null : <CalendarDays size={14} aria-hidden="true" />}
            {!iconOnly ? (
              <>
                <Text as="span" truncate>
                  {!isActive
                    ? selectionMode === 'single'
                      ? 'Set date'
                      : 'Date range'
                    : selectionMode === 'single'
                      ? formatRangeDate(value.endDate, true)
                      : value.key === 'custom'
                        ? formatRange(value.startDate, value.endDate)
                        : value.label}
                </Text>
                <ChevronDown size={11} aria-hidden="true" />
              </>
            ) : null}
          </Button>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner zIndex="dropdown">
            <Popover.Content
              layerStyle="surfacePopover"
              w={
                selectionMode === 'single'
                  ? 'min(340px, calc(100vw - 24px))'
                  : { base: 'min(360px, calc(100vw - 24px))', md: '660px' }
              }
              maxH="min(var(--available-height), 620px)"
              overflowY="auto"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="ui.border.default"
              borderRadius="14px"
              bg="ui.surface.card"
              p="0"
            >
              <Flex direction={{ base: 'column', md: 'row' }} minW={0}>
                {selectionMode === 'range' ? (
                  <VStack
                    align="stretch"
                    gap="1"
                    w={{ base: 'full', md: '150px' }}
                    p="2"
                    borderBottomWidth={{ base: '1px', md: '0' }}
                    borderRightWidth={{ base: '0', md: '1px' }}
                    borderColor="ui.border.subtle"
                    flexShrink={0}
                  >
                    <Text px="2" py="1" color="ui.text.tertiary" fontSize="2xs" fontWeight="800">
                      Quick ranges
                    </Text>
                    <Flex direction={{ base: 'row', md: 'column' }} gap="1" overflowX="auto">
                      {presets.map(preset => (
                        <Button
                          key={preset.key}
                          size="xs"
                          variant="ghost"
                          minW={{ base: 'auto', md: 'full' }}
                          justifyContent="space-between"
                          color={
                            isActive && value.key === preset.key
                              ? 'ui.text.primary'
                              : 'ui.text.secondary'
                          }
                          bg={
                            isActive && value.key === preset.key
                              ? 'ui.surface.selected'
                              : 'transparent'
                          }
                          onClick={() => {
                            onChange(dateRangeFromPreset(preset, today));
                            setOpen(false);
                          }}
                        >
                          {preset.label}
                          {isActive && value.key === preset.key ? <Check size={12} /> : null}
                        </Button>
                      ))}
                    </Flex>
                  </VStack>
                ) : null}

                <Box flex="1" minW={0} p={{ base: '3', md: '4' }}>
                  <HStack justify="space-between" mb="3">
                    <Box>
                      <Text color="ui.text.primary" fontSize="sm" fontWeight="800">
                        {selectionMode === 'single' ? 'Choose a due date' : 'Custom range'}
                      </Text>
                      <Text color="ui.text.tertiary" fontSize="xs">
                        {selectionMode === 'single'
                          ? draftEnd
                            ? formatRangeDate(draftEnd, true)
                            : 'No date selected'
                          : selectingEnd
                            ? 'Choose an end date'
                            : formatRange(draftStart, draftEnd)}
                      </Text>
                    </Box>
                    <HStack gap="1">
                      <Button
                        size="xs"
                        variant="ghost"
                        aria-label="Previous month"
                        onClick={() => setVisibleMonth(month => addMonths(month, -1))}
                      >
                        <ChevronLeft size={15} />
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        aria-label="Next month"
                        disabled={
                          !allowFutureDates &&
                          dateKey(visibleMonth).slice(0, 7) >= today.slice(0, 7)
                        }
                        onClick={() => setVisibleMonth(month => addMonths(month, 1))}
                      >
                        <ChevronRight size={15} />
                      </Button>
                    </HStack>
                  </HStack>
                  <Flex gap="4" minW={0}>
                    <MonthCalendar
                      month={visibleMonth}
                      startDate={draftStart}
                      endDate={draftEnd}
                      maxDate={allowFutureDates ? null : today}
                      onSelect={selectDay}
                    />
                    {selectionMode === 'range' ? (
                      <Box display={{ base: 'none', md: 'block' }} flex="1" minW={0}>
                        <MonthCalendar
                          month={addMonths(visibleMonth, 1)}
                          startDate={draftStart}
                          endDate={draftEnd}
                          maxDate={allowFutureDates ? null : today}
                          onSelect={selectDay}
                        />
                      </Box>
                    ) : null}
                  </Flex>

                  <Flex
                    mt="4"
                    pt="3"
                    borderTopWidth="1px"
                    borderColor="ui.border.subtle"
                    align={{ base: 'stretch', sm: 'center' }}
                    justify="space-between"
                    direction={{ base: 'column', sm: 'row' }}
                    gap="3"
                  >
                    {selectionMode === 'range' ? (
                      <Box minW={0}>
                        <Text color="ui.text.secondary" fontSize="xs" fontWeight="700">
                          {formatRange(draftStart, draftEnd)}
                        </Text>
                        {draftComparison ? (
                          <Text color="ui.text.tertiary" fontSize="2xs">
                            Previous period:{' '}
                            {formatRange(draftComparison.startDate, draftComparison.endDate)}
                          </Text>
                        ) : null}
                      </Box>
                    ) : (
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={!allowClear || !isActive}
                        onClick={() => {
                          onChange({
                            key: 'none',
                            label: 'No date',
                            startDate: '',
                            endDate: '',
                          });
                          setOpen(false);
                        }}
                      >
                        Clear date
                      </Button>
                    )}
                    <HStack gap="2" justify="flex-end">
                      <Button size="xs" variant="ghost" onClick={() => setOpen(false)}>
                        Cancel
                      </Button>
                      {selectionMode === 'range' ? (
                        <Button size="xs" variant="solid" onClick={applyCustomRange}>
                          Apply range
                        </Button>
                      ) : (
                        <Button size="xs" variant="solid" onClick={() => selectDay(today)}>
                          Today
                        </Button>
                      )}
                    </HStack>
                  </Flex>
                </Box>
              </Flex>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    </HStack>
  );
}
